import { ViewCellMapRow, eRowState } from "../View/ViewCellMapRow";
import { Gameplay, TIMER, eBatchGroup } from "../../Gameplay";
import { TileBase } from "../Ground Tiles/TileBase";
import { GameWorld } from "../GameWorld";
import { Player } from "../Player/Player";
import { Game, eAtlas_0, rnd } from "../../../Game";
import { TextureAtlasUtils } from "../../../Utils/TextuAtlasUtils";



export const enum eBonusType {
    point,
    scoreMultiplier,
}



export class BonusType {
    /**
     * (RO) Typ bonusu
     */
    get type(): eBonusType { return this._type; }
    private _type: eBonusType;

    /**
     * (RO) Asset s modelem pro bonus
     */
    get modelAsset(): pc.Asset { return this._modelAsset; }
    private _modelAsset: pc.Asset;

    /**
     * (RO) Rotace ve stupnich za vterinu
     */
    get rotation(): pc.Vec3 { return this._rotation; }
    private _rotation: pc.Vec3;

    /**
     * (RO) Polomer kolizni koule
     */
    get colRadius(): number { return this._colRadius; }
    private _colRadius: number;

    /**
     * (RO) Scale stinu
     */
    get shadowScale(): number { return this._shadowScale; }
    private _shadowScale: number;

    /**
     * (RO) Offset od zeme (na ose Y)
     */
    get groundOffset(): number { return this._groundOffset; }
    private _groundOffset: number;

    /**
     * (RO) Hodnota, o kterou se navysi skore po sebrani bonusu
     */
    get score(): number { return this._score; }
    private _score: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(type: eBonusType, score: number, modelAsset: string, colRadius: number, groundOffset: number, shadowScale: number, rotation: pc.Vec3) {
        this._type = type;
        this._score = score;
        this._modelAsset = Game.app.assets.find(modelAsset, pc.ASSET_MODEL);
        this._colRadius = colRadius;
        this._groundOffset = groundOffset;
        this._shadowScale = shadowScale;
        this._rotation = rotation;
    }
}



export class Bonus {
    /**
     * Udalost vyvolana po sebrani bonusu
     * Emitter: Gameplay.events
     * Parametry:
     * - this: Bonus
     * - pos: pc.Vec3
     */
    static readonly EVENT_PICKED_UP = "bns:picked";

    /**
     * Radka, do ktere bonus patri
     */
    protected _row: ViewCellMapRow;

    /**
     * Entita zobrazujici bonus
     */
    protected _entity: pc.Entity;

    /**
     * Entita zobrazujici stin
     */
    protected _shadow: pc.Entity;

    /**
     * (RO) Typ bonusu
     */
    get type(): BonusType { return this._type; }
    protected _type: BonusType;

    /**
     * (RO) Index skupiny bonusu
     */
    get groupId(): number { return this._groupId; }
    protected _groupId: number;

    /**
     * Cas sebrani bonusu hrace
     */
    private _pickupTime: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        // Vytvorit entitu
        this._entity = new pc.Entity();
        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.solid_1],
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false,
            lightmapped: false,
        });
        this._entity.enabled = false;

        // Stin
        let atlas = Game.getTextureAtlas(0);

        this._shadow = new pc.Entity();
        this._shadow.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_IMAGE,
            texture: atlas.texture,
            width: 0.5,
            height: 0.5,
            anchor: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 0.5],
            batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.fakeShadows]
        });
        this._shadow.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames[eAtlas_0.shadowCircle]).rect);
        this._shadow.setLocalEulerAngles(-90, 0, 0);
        this._shadow.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(type: BonusType, groupId: number, cellId: number, row: ViewCellMapRow): void {
        this._row = row;
        this._groupId = groupId;

        // Nastavit entitu
        if (this._type != type) {
            this._type = type;
            this._entity.model.asset = type.modelAsset;
        }

        let x = cellId * TileBase.SIZE + TileBase.SIZE / 2;

        this._entity.setLocalPosition(x, type.groundOffset, 0);
        this._entity.enabled = true;

        let rowGraphNode = row.graphNode;

        if (this._entity.parent != rowGraphNode)
            this._entity.reparent(rowGraphNode);

        // Pocatecni rotace
        let rotation = type.rotation;

        this._entity.setLocalEulerAngles(rotation.x != 0 ? rnd.randomIntIn(0, 359) : 0,
            rotation.y != 0 ? rnd.randomIntIn(0, 359) : 0,
            rotation.z != 0 ? rnd.randomIntIn(0, 359) : 0);

        // Stin
        if (this._shadow.parent != rowGraphNode)
            this._shadow.reparent(rowGraphNode);


        let i = type.shadowScale;

        this._shadow.setLocalScale(i, i, i);
        this._shadow.setLocalPosition(x, GameWorld.SHADOW_Y, 0);
        this._shadow.enabled = true;

        this._pickupTime = -1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    release(): void {
        this._entity.enabled = false;
        this._shadow.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        // Hlidat zajeti radky s bonusem pod view
        if (this._row.state == eRowState.deactivation) {
            this.release();
            this._row.decActBonuses();

            return false;
        }

        // Rotace
        let deltaS = TIMER.deltaS;
        let rotation = this._type.rotation;

        this._entity.rotate(rotation.x * deltaS, rotation.y * deltaS, rotation.z * deltaS);

        if (this._pickupTime < 0) {
            // Test kolize s hracem
            let pl = Player.instance;

            if (pl.checkCollisions) {
                let plPos = pl.position;
                let pos = this._entity.getPosition();
                let maxColDis = Player.COL_RADIUS + this._type.colRadius;

                if (Math.abs(plPos.z - pos.z) <= maxColDis &&
                    Math.abs(plPos.x - pos.x) <= maxColDis &&
                    Math.abs(plPos.y - pos.y) <= maxColDis) {

                    // Sebrat pickup
                    this._shadow.enabled = false;
                    this._pickupTime = TIMER.time;
                    Gameplay.instance.events.fire(Bonus.EVENT_PICKED_UP, this, pos);
                }
            }
        }
        else {
            let progress = (TIMER.time - this._pickupTime) / 750;
            let pos = this._entity.getLocalPosition();

            if (progress >= 1) {
                this.release();
                return false;
            }

            this._entity.setLocalPosition(pos.x, GameWorld.GROUND_Y + this._type.groundOffset + (2 * pc.CubicOut(progress)), 0)
        }

        return true;
    }
}